// PlyrPlayer.jsx
import React, { useEffect, useRef } from "react";
import Hls from "hls.js";
import Plyr from "plyr";
import "plyr/dist/plyr.css";
import { useLocation } from "react-router-dom";

const PlyrPlayer = () => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const hlsRef = useRef(null);

  const location = useLocation();
  const { m3u8Url, notesUrl, isLive } = location.state || {};

  // === Gestures ===
  const attachGestures = (player, video) => {
    const container = video.parentElement;
    let holdTimeout = null;
    let speedHeld = false;
    let lastTap = 0;

    const handleTouchStart = () => {
      holdTimeout = setTimeout(() => {
        if (!speedHeld) {
          speedHeld = true;
          player.speed = 2;
        }
      }, 1000);
    };
    const handleTouchEnd = () => {
      clearTimeout(holdTimeout);
      if (speedHeld) {
        player.speed = 1;
        speedHeld = false;
      }
    };

    const handleDoubleTap = (e) => {
      const now = Date.now();
      const tapGap = now - lastTap;
      lastTap = now;
      if (tapGap < 300) {
        const { clientX } = e.changedTouches[0];
        const { left, width } = container.getBoundingClientRect();
        const tapX = clientX - left;
        if (tapX < width / 3) player.currentTime -= 10;
        else if (tapX > (2 * width) / 3) player.currentTime += 10;
        else player.togglePlay();
      }
    };

    video.addEventListener("touchstart", handleTouchStart);
    video.addEventListener("touchend", handleTouchEnd);
    container.addEventListener("touchend", handleDoubleTap);
  };

  useEffect(() => {
    if (!videoRef.current || !m3u8Url) return;

    const video = videoRef.current;
    let hls;
    let player;

    if (Hls.isSupported()) {
      hls = new Hls();
      hls.loadSource(m3u8Url);
      hls.attachMedia(video);
      hlsRef.current = hls;

      player = new Plyr(video, {
        controls: [
          "play-large",
          "rewind",
          "play",
          "fast-forward",
          "progress",
          "current-time",
          "duration",
          "mute",
          "volume",
          "settings",
          "fullscreen",
        ],
        settings: ["quality", "speed"],
        speed: { selected: 1, options: [0.5, 1, 1.25, 1.5, 2] },
        quality: {
          default: 720,
          options: [], // will be injected below
          forced: true,
          onChange: (q) => {
            const level = hls.levels.findIndex(
              (l) => l.height === parseInt(q, 10)
            );
            if (level !== -1) hls.currentLevel = level;
          },
        },
      });

      playerRef.current = player;

      // 🔑 THIS is what actually injects qualities
      hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
        const available = data.levels
          .map((l) => l.height)
          .filter((v, i, a) => a.indexOf(v) === i) // unique
          .sort((a, b) => b - a);

        if (available.length > 0) {
          player.options.quality.options = available;
          player.quality = available[0]; // pick highest by default
        }
      });

      attachGestures(player, video);
    } else {
      // Safari native
      video.src = m3u8Url;
      player = new Plyr(video);
      playerRef.current = player;
      attachGestures(player, video);
    }

    return () => {
      if (playerRef.current) playerRef.current.destroy();
      if (hlsRef.current) hlsRef.current.destroy();
    };
  }, [m3u8Url]);

  if (!m3u8Url) return <div style={{ color: "#fff" }}>❌ No video URL</div>;

  return (
    <div style={{ background: "#000", minHeight: "100vh", paddingTop: 20 }}>
      <div style={{ position: "relative" }}>
        <video ref={videoRef} playsInline controls autoPlay></video>
        {isLive && (
          <div
            style={{
              position: "absolute",
              top: 15,
              right: 15,
              background: "rgba(0,0,0,0.5)",
              color: "#fff",
              padding: "4px 8px",
              borderRadius: 4,
              fontSize: 12,
            }}
          >
            LIVE
          </div>
        )}
      </div>

      <div style={{ textAlign: "center", marginTop: 20 }}>
        {notesUrl && (
          <a
            href={notesUrl}
            target="_blank"
            rel="noreferrer"
            style={{
              margin: "0 10px",
              padding: "10px 20px",
              background: "#007bff",
              color: "#fff",
              borderRadius: 5,
              textDecoration: "none",
            }}
          >
            📄 Notes
          </a>
        )}
      </div>
    </div>
  );
};

export default PlyrPlayer;
